import type { Pattern } from '../pipeline/pipeline';
import { isIndexedDbAvailable, loadProject, saveProject, type ProjectRecord } from './indexeddb';
import { serializePattern } from './serialize';

export type AutosaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export type AutosaveController = {
  /** Demande un autosave (sera debounced + executé de façon asynchrone). */
  trigger(pattern: Pattern, name: string): void;
  /** Force un flush immédiat du timer (utile pour onDestroy). */
  flush(): Promise<void>;
  /** Annule le timer en cours sans sauver. */
  cancel(): void;
  /** Statut courant — lecture seule, observé par l'UI. */
  getStatus(): AutosaveStatus;
  /** Souscrire aux changements de statut. Retourne une fonction unsubscribe. */
  onStatusChange(listener: (status: AutosaveStatus) => void): () => void;
  /** Timestamp du dernier autosave réussi (ms epoch). 0 si jamais sauvé. */
  getLastSavedAt(): number;
};

/**
 * Crée un contrôleur d'autosave debouncé.
 *
 * Usage type (Editor.svelte) :
 *   const auto = createAutosave('current', 'Projet sans nom', 1000);
 *   $effect(() => { void pattern; auto.trigger(pattern, 'Mon projet'); });
 *   onDestroy(() => { auto.flush(); });
 *
 * Comportement :
 *  - `trigger` (ré)arme un setTimeout de `debounceMs` ms.
 *  - Statut : `pending` immédiatement, puis `saving` au moment du write,
 *    puis `saved` ou `error`.
 *  - Si IndexedDB indisponible : statut bascule en `error` silencieusement,
 *    aucune exception remontée à l'UI.
 */
export function createAutosave(projectId: string, debounceMs = 1000): AutosaveController {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pendingPattern: Pattern | null = null;
  let pendingName: string = '';
  let createdAt: number | null = null;
  let lastSavedAt = 0;
  let status: AutosaveStatus = 'idle';
  const listeners = new Set<(s: AutosaveStatus) => void>();

  function setStatus(s: AutosaveStatus): void {
    status = s;
    for (const l of listeners) l(s);
  }

  async function performSave(): Promise<void> {
    if (!pendingPattern) return;
    const p = pendingPattern;
    const n = pendingName;
    pendingPattern = null;
    if (!isIndexedDbAvailable()) {
      setStatus('error');
      return;
    }
    setStatus('saving');
    try {
      const now = Date.now();
      if (createdAt === null) {
        // Préserve la date de création du record existant (le brouillon
        // restauré a pu être créé bien avant cette session).
        const existing = await loadProject(projectId);
        createdAt = existing?.createdAt ?? now;
      }
      const record: ProjectRecord = {
        id: projectId,
        name: n,
        pattern: serializePattern(p),
        createdAt,
        updatedAt: now,
      };
      await saveProject(record);
      lastSavedAt = now;
      setStatus('saved');
    } catch (err) {
      // Surface l'erreur exacte pour debug : sans ça, on ne sait pas si c'est
      // openDb (VersionError, QuotaExceededError…) ou put() (DataCloneError…).
      console.error('[autosave] Échec de la sauvegarde :', err);
      setStatus('error');
    }
  }

  return {
    trigger(pattern, name) {
      pendingPattern = pattern;
      pendingName = name;
      if (timer !== null) clearTimeout(timer);
      setStatus('pending');
      timer = setTimeout(() => {
        timer = null;
        void performSave();
      }, debounceMs);
    },
    async flush(): Promise<void> {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      await performSave();
    },
    cancel(): void {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      pendingPattern = null;
      if (status === 'pending') setStatus('idle');
    },
    getStatus(): AutosaveStatus {
      return status;
    },
    onStatusChange(listener) {
      listeners.add(listener);
      return (): void => {
        listeners.delete(listener);
      };
    },
    getLastSavedAt(): number {
      return lastSavedAt;
    },
  };
}
